import { platformApiClient } from './platform.api.client';

/**
 * Preferences for the platform console, kept on the server.
 *
 * A separate store from the HRMS one because the platform operator is a separate identity with its
 * own session cookie: the request has to carry the platform marker, so it goes through the platform
 * client. Same shape, same rules — memory for the page, one row per key on the server — and the
 * console's two cosmetic choices are the only things in it.
 */
const memory: Record<string, string> = {};
let loaded = false;
const listeners = new Set<() => void>();

export function loadPlatformPreferences(force = false): Promise<void> {
  if (loaded && !force) return Promise.resolve();
  return platformApiClient.get<{ user: Record<string, string> }>('/UserPreferences')
    .then(res => {
      Object.keys(memory).forEach(k => delete memory[k]);
      Object.assign(memory, res.data?.user ?? {});
      loaded = true;
      listeners.forEach(l => l());
    })
    .catch(() => { /* defaults apply until the server answers */ });
}

export function clearPlatformPreferences(): void {
  Object.keys(memory).forEach(k => delete memory[k]);
  loaded = false;
  listeners.forEach(l => l());
}

export function onPlatformPreferencesChanged(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

export function getPlatformPref<T>(key: string, fallback: T): T {
  const raw = memory[key];
  if (raw === undefined) return fallback;
  try { return JSON.parse(raw) as T; } catch { return fallback; }
}

export function setPlatformPref<T>(key: string, value: T): void {
  memory[key] = JSON.stringify(value);
  listeners.forEach(l => l());
  platformApiClient.put(`/UserPreferences/${encodeURIComponent(key)}?scope=user`, { valueJson: JSON.stringify(value) })
    .catch(() => { /* the in-memory copy stands for this page */ });
}
